import { callFirebaseFunction } from 'boot/firebase';
import SelectClientUser from 'components/UI/modals/SelectClientUser';
import StrictConfirmDialog from 'components/UI/modals/StrictConfirmDialog';
import { serverTimestamp } from 'firebase/firestore';
import { chain, find, get, head, includes, isEmpty, map, union, xor } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { POLLING_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import { SESSION_TYPES } from 'src/constants/sessions';
import AppointmentsDB from 'src/fb/appointments-db';
import ClientFilesDB from 'src/fb/clientFiles-db';
import NotificationSettingsDb from 'src/fb/notificationSettings-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionsDB from 'src/fb/sessions-db';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import Time from 'src/mixins/Time';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { sessions } from 'src/store/sessions';
import AppUtils, { copySupportInfoToClipboard } from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';

export default {

  mixins: [
    Auth,
    Interactions,
    Time,
  ],

  props: {
    session: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      weekdays: map(this.$tm('common.weekdays'), (weekday) => weekday.substring(0, 3)),

      visibilitySettings: {
        limitedVisibility: false,
        visibility: [],
        limitHistoryVisibility: false,
      },
      visibilitySettingsChanged: false,

      videoSessionVisibilitySettings: {
        videoSessionLimitedVisibility: false,
        videoSessionVisibility: [],
        defaultRolesForVideoCall: true,
      },

      videoSessionVisibilitySettingsChanged: false,

      notificationSettings: {
        domain: 'session',
        session: this.session.id,
        type: 'none',
        custom: [],
        notifyImmediately: false,
      },

    };
  },

  emits: ['edit', 'edit-schedule', 'updated'],

  computed: {
    ...mapState(auth, ['connectedClinic', 'userDetails', 'visibilityOptions', 'hasSupervisorCredentials']),
    ...mapState(network, ['offlineMode']),

    userCanSeeMenu() {
      return this.userCanEditScheduledSession;
    }, // userCanSeeMenu()

    userCanEditScheduledSession() {
      return this.userHasPrivilege('sessions/editScheduledSessions');
    }, // userCanEditScheduledSession()

    userCanEditSessionVisibility() {
      return this.userHasPrivilege('sessions/editScheduledSessions');
    }, // userCanEditSessionVisibility()

    userCanDeleteScheduledSession() {
      return this.userHasPrivilege('sessions/deleteScheduledSessions');
    }, // userCanDeleteScheduledSession()

    userCanEditSessionTime() {
      return this.hasSupervisorCredentials;
    },

    sessionIsRunning() {
      return (this.session.status === 'running')
        || (this.session.supervisedBy === this.userDetails.userId);
    },

    hasNotificationSettings() {
      return ((this.session.status === 'created') || (this.session.status === 'scheduled'));
    }, // hasNotificationSettings()

    sessionNotificationIcon() {
      const sessionNotificationType = get(this.notificationSettings, 'type', 'none');

      if (sessionNotificationType === 'all') {
        return 'notifications_active';
      }
      if (sessionNotificationType === 'custom') {
        return 'notifications_none';
      }
      return 'notifications_off';
    }, // sessionNotificationIcon()

    sessionNameFormatted() {
      return this.session.sessionName || this.$t('sessions.sessionCard.adHocSessionTitle');
    },

    currentWeekday() {
      return this.getDay(new Date());
    },

    firstDayOfWeek() {
      const newDate = new Date();
      return this.adjustDate(
        this.subtractFromDate(newDate, { days: this.getDay(newDate) }),
        { hour: 0, minute: 0, second: 0 },
      );
    },

    sessionHistoryPerDay() {
      if (!this.session.sessionHistory) {
        return [];
      }

      const newDate = new Date();
      const firstDay = this.adjustDate(newDate, {
        day: this.getDate(newDate) - this.currentWeekday,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      return chain(this.session.sessionHistory)
        .filter((sessionHistoryItem) => sessionHistoryItem.finishTimestamp.toDate() >= firstDay)
        .map((sessionHistoryItem) => this.getDay(sessionHistoryItem.finishTimestamp))
        .uniq()
        .value();
    }, // sessionHistoryThisWeek()

  },

  methods: {
    ...mapActions(sessions, ['deleteSession']),

    /**
     * Delete a session
     */
    async onDeleteSession() {
      const message = this.session.sessionType === 'form'
        ? this.$t('sessions.sessionCard.notifyDeleteScheduledFormConfirm', {
          formName: this.session.sessionName,
        })
        : this.$t('sessions.sessionCard.notifyDeleteScheduledSessionConfirm', {
          sessionName: this.session.sessionName,
        });

      this.openComponentDialog(
        StrictConfirmDialog,
        {
          title: this.$t('sessions.sessionCard.dialogDeleteScheduledSessionTitle', {
            sessionName: this.session.sessionName,
          }),
          target: 'DELETE',
          message,
          okLabel: this.$t('common.buttonDelete'),
        },
        async () => {
          await this.handleDeleteSession();
          await this.handleDeleteSessionAppointments();
          await this.handleDeleteSessionClientFiles();
        },
      );
    }, // onDeleteSession()

    async onReassignForm() {
      this.openComponentDialog(
        SelectClientUser,
        {
          title: this.$t('sessions.sessionCard.textReassignForm'),
          label: this.$t('sessions.sessionCard.textReassignFormLabel'),
          text: '',
          okButton: this.$t('sessions.sessionCard.textReassignFormOkButton'),
          skipRoles: this.hiddenRoles,
          skipCurrentUser: false,
        },
        async (user) => {
          if (isEmpty(user)) {
            return;
          }

          const sessionActivitiesDB = new SessionActivitiesDB();
          const sessionsDb = new SessionsDB();

          try {
            this.$q.loading.show();

            const updateActions = [];

            // Check if session activity exists for this form session
            const sessionActivities = await sessionActivitiesDB.readAll([
              ['type', '==', 'form'],
              ['session', '==', this.session.id],
              ['client', '==', this.session.client],
              ['clinic', '==', this.session.clinic],
              ['isDeleted', '==', false],
            ]);

            if (!isEmpty(sessionActivities)) {
              updateActions.push(...map(sessionActivities, (sessionActivitiy) => {
                return sessionActivitiesDB.set(
                  {
                    user: user.user,
                    userName: user.userName,
                  },
                  sessionActivitiy.id,
                );
              }));
            }

            updateActions.push(
              sessionsDb.set(
                {
                  runBy: user.user,
                  runByName: user.userName,
                },
                this.session.id,
              ),
            );

            await Promise.all(updateActions);

            this.notifySuccess(this.$t('sessions.sessionCard.notifyReassignFormSuccess', { userName: user.userName }));
          } catch (err) {
            this.notifyNegative(err?.message ?? err);
            console.error(err);
            logEvent(err);
          } finally {
            this.$q.loading.hide();
          }
        },
      );
    }, // onReassignForm()

    async handleDeleteSession() {
      this.$q.loading.show();
      try {
        await this.deleteSession(this.session.id);
        if (this.session.sessionType === SESSION_TYPES.FORM) {
          this.notifySuccess(this.$t('sessions.sessionCard.notifyDeleteFormSuccess'));
        } else {
          this.notifySuccess(this.$t('sessions.sessionCard.notifyDeleteSessionSuccess'));
        }
        this.$emit('updated', this.session.id, null);
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // handleDeleteSession()

    async handleDeleteSessionAppointments() {
      const appointmentsDb = new AppointmentsDB();
      const currentSessionUserAppointments = await appointmentsDb.readAll([
        ['clinic', '==', this.userDetails.clinic],
        ['session', '==', this.session.id],
        ['isDeletedSession', '==', false],
      ]);

      if (isEmpty(currentSessionUserAppointments)) {
        return;
      }

      this.$q.loading.show();

      try {
        const deleteActions = currentSessionUserAppointments.map((appointment) => {
          // appointment is from an integration
          if ('integrationReference' in appointment || 'integrationData' in appointment) {
            return appointmentsDb.update(
              {
                id: appointment.id,
                isDeletedSession: true,
                deletedTimestamp: serverTimestamp(),
              },
            );
          }

          return appointmentsDb.delete(appointment.id);
        });

        await AppUtils.allSettled(deleteActions, (err) => this.notifyNegative(err?.message ?? err));

        const clientsToRemap = currentSessionUserAppointments.reduce((acc, appointment) => {
          if (
            appointment.integration
            && !POLLING_DRIVEN_INTEGRATIONS.includes(appointment.integration)
            && appointment.client
          ) {
            acc.push(appointment.client);
          }

          return acc;
        }, []);

        if (clientsToRemap.length) {
          await callFirebaseFunction('mapSessionWithAppointments', {
            clientsIds: clientsToRemap,
            resetDeletedSessions: true,
          });
        }
      } finally {
        this.$q.loading.hide();
      }
    }, // handleDeleteSessionAppointments()

    async handleDeleteSessionClientFiles() {
      if (!this.session.clientFile) {
        return;
      }

      this.$q.loading.show();

      try {
        const clientFilesDB = new ClientFilesDB();
        await clientFilesDB.delete(this.session.clientFile);
      } catch (error) {
        logEvent(error);
        this.notifyNegative(error.message);
      } finally {
        this.$q.loading.hide();
      }
    },

    onEditSession() {
      this.$emit('edit', this.session);
    }, // onEditSession()

    onEditSessionSchedule() {
      this.$emit('edit-schedule', this.session);
    }, // onEditSessionSchedule()

    onCloseCustomNotificationMenu() {
      if (this.notificationSettings.type === 'custom' && isEmpty(this.notificationSettings.custom)) {
        this.notificationSettings.type = 'none';
      }
      this.saveNotificationSettings();
    },

    onUpdateNotifications(notifications) {
      this.notificationSettings = {
        ...this.notificationSettings,
        ...notifications,
      };
    }, // onUpdateNotifications()

    onCloseVisibilityMenu() {
      this.saveVisibilitySettings();
      this.saveVideoSessionVisibilitySettings();
    }, // onCloseVisibilityMenu()

    onToggleVisibility(visibilityItem) {
      this.visibilitySettings = {
        ...this.visibilitySettings,
        visibility: xor(this.visibilitySettings.visibility, [visibilityItem]),
      };
      this.visibilitySettingsChanged = true;
    }, // onToggleVisibility()

    onUpdateVisibilitySetting(visibilitySetting, overrideHistoryVisibility = false) {
      const visibilityUpdates = {};

      if (get(visibilitySetting, 'limitedVisibility')) {
        const userPrivilege = this.userDetails.userPrivilege || this.userDetails.role;
        if (find(this.visibilityOptions, { value: userPrivilege })) {
          visibilityUpdates.visibility = union(this.visibilitySettings.visibility, [userPrivilege]);
        }
      }

      // in some places on UI we show limitHistoryVisibility toggle only if limitedVisibility is true, so
      // we should set limitHistoryVisibility to false if limitedVisibility is false in these cases to avoid confusions
      if (
        overrideHistoryVisibility
        && get(visibilitySetting, 'limitedVisibility') === false
        && get(this.visibilitySettings, 'limitHistoryVisibility')
      ) {
        visibilityUpdates.limitHistoryVisibility = false;
      }

      this.visibilitySettings = {
        ...this.visibilitySettings,
        ...visibilitySetting,
        ...visibilityUpdates,
      };
      this.visibilitySettingsChanged = true;
    }, // onUpdateVisibilitySetting()

    onCopySupportInfo(additionalInfo = {}) {
      copySupportInfoToClipboard({ session: this.session?.id, ...additionalInfo });
    },

    setVisibilitySettings(session) {
      this.visibilitySettings = {
        limitedVisibility: get(session, 'limitedVisibility', false),
        visibility: get(session, 'visibility', []),
        limitHistoryVisibility: get(session, 'limitHistoryVisibility', false),
      };
    }, // setVisibilitySettings()

    async saveVisibilitySettings() {
      const id = get(this.session, 'id', null);

      if (!id || !this.visibilitySettingsChanged) {
        return;
      }

      const sessionsDb = new SessionsDB();
      sessionsDb.set(this.visibilitySettings, id);
      this.visibilitySettingsChanged = false;
    }, // saveVisibilitySettings()

    async saveVideoSessionVisibilitySettings() {
      const id = get(this.session, 'id', null);

      if (!id || !this.videoSessionVisibilitySettingsChanged) {
        return;
      }

      const sessionsDb = new SessionsDB();
      sessionsDb.set(this.videoSessionVisibilitySettings, id);
      this.videoSessionVisibilitySettingsChanged = false;
    }, // saveVideoSessionVisibilitySettings()

    async loadNotificationSettings(sessionId) {
      if (!this.hasNotificationSettings || this.offlineMode) {
        return;
      }

      const notificationSettingsDb = new NotificationSettingsDb();
      const retval = await notificationSettingsDb.readAll([
        ['session', '==', sessionId],
        ['user', '==', this.userDetails.userId],
      ]);

      if (!isEmpty(retval)) {
        this.notificationSettings = { ...this.notificationSettings, ...head(retval) };
      }
    }, // loadNotificationSettings()

    async saveNotificationSettings() {
      this.notificationSettings.user = this.userDetails.userId;

      const id = get(this.notificationSettings, 'id', null);

      const notificationSettingsDb = new NotificationSettingsDb();
      const retval = await notificationSettingsDb.create(this.notificationSettings, id);

      if (retval) {
        this.notificationSettings = retval;
      }
    }, // saveNotificationSettings()

    weekdaySize(weekday) {
      return (weekday === this.currentWeekday) ? 'sm' : 'xs';
    }, // weekdaySize()

    weekdayClass(weekday) {
      let weekdayClass = '';

      if ((weekday === this.currentWeekday)) {
        weekdayClass = 'text-bold';
      }

      if ((chain(this.session).get(['scheduledTimestamp', 'toDate']).isFunction().value())
        && (this.firstDayOfWeek < this.session.scheduledTimestamp.toDate())
        && (weekday < this.getDay(this.session.scheduledTimestamp))) {
        weekdayClass += ' text-grey-4';
      } else if (this.sessionHistoryPerDay.includes(weekday)) {
        weekdayClass += ' bg-positive text-white';
      } else if (includes(this.session.recurringWeekdays, weekday)) {
        if ((weekday < this.currentWeekday) && (!this.sessionHistoryPerDay.includes(weekday))) {
          weekdayClass += ' bg-red-6 text-white';
        }
      } else {
        // Weekday not scheduled for sessions
        weekdayClass += ' text-grey-6';
      }

      return weekdayClass;
    }, // weekdayClass()

    weekdayHasSession(weekday) {
      return (this.sessionHistoryPerDay.includes(weekday));
    }, // weekdayHasSession()

  },
};
